"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { useHydrationSafeProfile } from "src/app/AuthenticatedProfileProvider";
import { DEFAULT_AUTHENTICATED_PATH } from "src/lib/auth-routing";
import { UserAvatar } from "./UserAvatar";

const navbarItems = [
  {
    title: "Projects",
    icon: "/pursuits.svg",
    compare: "/projects",
    href: DEFAULT_AUTHENTICATED_PATH,
  },
  {
    title: "Services",
    icon: "/services.svg",
    compare: "/s/",
    href: "/s/services",
  },
  {
    title: "Properties",
    icon: "/property.svg",
    compare: "/p/",
    href: "/p/assets",
    flag: "org_property_catalog",
  },
  {
    title: "Suppliers",
    icon: "/suppliers.svg",
    compare: "/org-suppliers",
    href: "/org-suppliers/supplier-list",
  },
  {
    title: "Staffing",
    icon: "/staffing.svg",
    compare: "/staffing",
    href: "/staffing",
  },
  {
    title: "Settings",
    icon: "/settings.svg",
    compare: "/settings/",
    href: "/settings/company-profile",
  },
  {
    title: "Help",
    icon: "/help.svg",
    compare: "n/a",
    href: "https://help.caltana.com",
  },
];

type SidebarItem = {
  title: string;
  icon: string;
  compare: string;
  href: string;
  flag?: string;
};

function SidebarListItem({
  nav,
  isShowSidebar,
}: Readonly<{
  nav: SidebarItem;
  isShowSidebar: boolean;
}>) {
  const pathname = usePathname();
  const isActive = pathname.startsWith(nav.compare);

  return (
    <li className="relative group">
      <Link
        href={nav.href}
        className={`
          flex items-center relative overflow-hidden
          mx-0 my-1 px-3 py-3 rounded-xl
          text-sm font-medium
          transition-all duration-300 ease-out
          ${
            isActive
              ? "bg-purple-100 text-white shadow-lg shadow-purple-100/30"
              : "text-dark-60 dark:text-gray-400 hover:text-dark-100 dark:hover:text-gray-200 hover:bg-white/80 dark:hover:bg-white/10 hover:shadow-md"
          }
        `}
      >
        <div className="flex items-center justify-center w-5 h-5 flex-shrink-0">
          <Image
            src={nav.icon}
            width={20}
            height={21}
            alt=""
            className={`transition-all duration-300 ${isActive ? "brightness-0 invert" : "dark-icon-purple-light"}`}
          />
        </div>

        <span
          className={`
            ml-4 whitespace-nowrap font-medium
            transition-all duration-300 ease-out
            ${isShowSidebar ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2"}
          `}
        >
          {nav.title}
        </span>

        {isActive && (
          <div className="absolute inset-0 bg-gradient-to-r from-purple-100 to-purple-60 rounded-xl opacity-10" />
        )}
      </Link>
    </li>
  );
}

export function Sidebar({
  smallLogo,
  largeLogo,
}: Readonly<{
  smallLogo: React.ReactNode;
  largeLogo: React.ReactNode;
}>) {
  const [showSidebar, setShowSidebar] = useState(false);
  // The profile decides how many nav items render and whether UserAvatar renders
  // at all, so it has to be the hydration-stable value rather than the live query.
  const user = useHydrationSafeProfile();

  const visibleNavItems = navbarItems.filter((nav) => !nav.flag || user?.featureFlags?.[nav.flag]);

  return (
    <>
      {/* Sidebar */}
      <nav
        className={`
          fixed left-0 top-0 h-screen z-50
          bg-gradient-to-b from-purple-5 via-white to-purple-5
          dark:from-gray-900 dark:via-gray-900 dark:to-gray-900
          backdrop-blur-xl
          border-r border-dark-100/20 dark:border-gray-700
          shadow-2xl shadow-dark-100/10 dark:shadow-black/30
          transition-all duration-300 ease-out
          px-2 py-5 flex flex-col items-start
          ${showSidebar ? "w-44" : "w-16"}
        `}
        onMouseLeave={() => setShowSidebar(false)}
        onMouseEnter={() => setShowSidebar(true)}
      >
        {/* Logo Section */}
        <Link href="/" aria-label="Caltana home" className="px-2 mb-8">
          <div className={`${showSidebar ? "hidden" : "block"}`}>{smallLogo}</div>
          <div className={`${showSidebar ? "block" : "hidden"}`}>{largeLogo}</div>
        </Link>

        {/* Navigation Items */}
        <ul className="list-none p-0 flex-grow w-full">
          {visibleNavItems.map((nav) => (
            <SidebarListItem key={nav.title} nav={nav} isShowSidebar={showSidebar} />
          ))}
        </ul>

        {/* User Profile Section - Inside Nav */}
        <div className="w-full px-1 mt-auto">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <UserAvatar user={user} />
            </div>

            <div
              className={`
                min-w-0 flex-1 overflow-hidden
                transition-all duration-300 ease-out
                ${showSidebar ? "opacity-100 translate-x-0 max-w-[120px]" : "opacity-0 -translate-x-2 max-w-0"}
              `}
            >
              <div className="font-semibold text-dark-100 dark:text-gray-200 truncate text-sm">
                {user?.name ?? "Loading..."}
              </div>
              <div className="text-xs text-dark-40 dark:text-gray-500 truncate">
                {user?.organization.name ?? ""}
              </div>
            </div>
          </div>
        </div>

        {/* Subtle gradient overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-dark-100/5 dark:to-white/5 pointer-events-none" />
      </nav>

      {/* Backdrop for mobile (optional enhancement) */}
      {showSidebar && (
        <div
          className="fixed inset-0 bg-dark-100/20 dark:bg-black/40 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setShowSidebar(false)}
        />
      )}
    </>
  );
}
